import { apiService } from './api';
import {
  AdminUser,
  Role,
  Permission,
  CreateUserRequest,
  UpdateUserRequest,
  CreateRoleRequest,
  UpdateRolePermissionsRequest,
  UserPermissions,
} from '../types/admin';

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

const requireData = <T>(response: ApiEnvelope<T>): T => {
  if (response?.data !== undefined && response.data !== null) {
    return response.data;
  }
  throw new Error(response?.message || 'Unexpected API response.');
};

const optionalArray = <T>(response: ApiEnvelope<T[]>): T[] => {
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return [];
};

export const adminService = {
  // User Management
  async getUsers(): Promise<AdminUser[]> {
    const response = await apiService.get<ApiEnvelope<AdminUser[]>>('/users');
    return optionalArray(response);
  },

  async getUser(id: string): Promise<AdminUser> {
    const response = await apiService.get<ApiEnvelope<AdminUser>>(`/users/${id}`);
    return requireData(response);
  },

  async createUser(data: CreateUserRequest): Promise<AdminUser> {
    const response = await apiService.post<ApiEnvelope<AdminUser>>('/users', data);
    return requireData(response);
  },

  async updateUser(id: string, data: UpdateUserRequest): Promise<AdminUser> {
    const response = await apiService.put<ApiEnvelope<AdminUser>>(`/users/${id}`, data);
    return requireData(response);
  },

  async deleteUser(id: string): Promise<void> {
    await apiService.delete(`/users/${id}`);
  },

  // Role Management
  async getRoles(): Promise<Role[]> {
    const response = await apiService.get<ApiEnvelope<Role[]>>('/roles');
    return optionalArray(response);
  },

  async getRole(id: string): Promise<Role> {
    const response = await apiService.get<ApiEnvelope<Role>>(`/roles/${id}`);
    return requireData(response);
  },

  async createRole(data: CreateRoleRequest): Promise<Role> {
    const response = await apiService.post<ApiEnvelope<Role>>('/roles', data);
    return requireData(response);
  },

  async updateRolePermissions(
    id: string,
    data: UpdateRolePermissionsRequest
  ): Promise<Role> {
    const response = await apiService.put<ApiEnvelope<Role>>(`/roles/${id}/permissions`, data);
    return requireData(response);
  },

  async deleteRole(id: string): Promise<void> {
    await apiService.delete(`/roles/${id}`);
  },

  // Permission Management
  async getPermissions(): Promise<Permission[]> {
    const response = await apiService.get<ApiEnvelope<Permission[]>>('/permissions');
    return optionalArray(response);
  },

  async getMyPermissions(): Promise<UserPermissions> {
    console.log('[adminService] Fetching my permissions...');
    const response = await apiService.get<any>('/permissions/my-permissions');
    console.log('[adminService] Raw response:', JSON.stringify(response, null, 2));
    console.log('[adminService] Response type:', typeof response);
    console.log('[adminService] Response keys:', Object.keys(response || {}));

    // Handle both wrapped and unwrapped responses
    const data = response?.data || response;
    console.log('[adminService] Extracted data:', JSON.stringify(data, null, 2));
    console.log('[adminService] Data type:', typeof data);
    console.log('[adminService] Data keys:', Object.keys(data || {}));
    console.log('[adminService] data.permissions type:', typeof data?.permissions, Array.isArray(data?.permissions));
    console.log('[adminService] data.grouped type:', typeof data?.grouped, Array.isArray(data?.grouped));
    console.log('[adminService] data.grouped value:', data?.grouped);
    console.log('[adminService] Has permissions?', data?.permissions !== undefined);
    console.log('[adminService] Has grouped?', data?.grouped !== undefined);

    if (data?.permissions !== undefined && data?.grouped !== undefined) {
      console.log('[adminService] Returning permissions:', data);
      return data;
    }

    console.error('[adminService] Unexpected response format:', { response, data });
    throw new Error('Failed to fetch my permissions: unexpected response format');
  },

  async checkPermission(page: string, action: string): Promise<boolean> {
    const response = await apiService.get<ApiEnvelope<{ hasPermission: boolean }>>(
      `/permissions/check/${page}/${action}`
    );
    const data = response.data;
    return data?.hasPermission ?? false;
  },
};

